"use client";
import React, { FC } from "react";
import IconTextListItem from "@/components/TextLists/IconTextList/IconTextListItem";
import { getCourseInformation } from "@/lib/course/utils/getCourseInformation";
import { ProcessedCourse } from "@/common/course/types";
import { type VariantColors } from "@/types/Factoid";
import { Skeleton } from "@/components/ui/skeleton";
import IconTextGrid from "@/components/TextLists/IconTextList/IconTextGrid";
import useTranslations from "@/i18n/useTranslations";

export type Props = {
  course: ProcessedCourse;
  paymentOption: string | null;
  hasDateColumn?: boolean;
  isLoading?: boolean;
  isArchivedExternally?: boolean;
  certificateFactoid?: {
    variantColor?: VariantColors;
    showFactoidDesktop: boolean;
    showFactoidMobile: boolean;
    showFactoid?: boolean;
    factoidText: string;
    factoidHeader: string;
    factoidIcon: string;
    tooltip?: string | null;
    position: number;
  };
};

const Loader: FC = () =>
  Array.from({ length: 3 }).map((_, i) => (
    <div key={i} className="flex items-center space-x-4 mr-36">
      <Skeleton className="h-12 w-12 rounded-full" />
      <div className="space-y-2">
        <Skeleton className="h-2 w-[150px]" />
        <Skeleton className="h-2 w-[200px]" />
      </div>
    </div>
  ));

export default function CourseSnapshot({
  course,
  paymentOption,
  hasDateColumn,
  isArchivedExternally = false,
  certificateFactoid,
}: Props) {
  const tCourse = useTranslations("Course");
  const resolvedCertificateFactoid: NonNullable<Props["certificateFactoid"]> & {
    factoidIconName?: string;
  } = certificateFactoid ?? {
    factoidText: tCourse("receiveCertificateOrBadge"),
    position: 4,
    showFactoidDesktop: true,
    showFactoidMobile: true,
    showFactoid: true,
    factoidHeader: tCourse("earnYourCredentials"),
    factoidIcon: "certificate",
    factoidIconName: "certificate",
    tooltip: null,
  };
  const courseInformation = getCourseInformation(
    course,
    paymentOption,
    hasDateColumn,
    isArchivedExternally,
    false,
    tCourse as unknown as (
      key: string,
      values?: Record<string, string | number | Date>,
    ) => string,
  );

  const showCertificateFactoid =
    resolvedCertificateFactoid.showFactoid === false ? false : true;

  if (showCertificateFactoid) {
    const factoidItem = {
      icon: resolvedCertificateFactoid.factoidIcon || "certificate",
      header: resolvedCertificateFactoid.factoidHeader,
      description: resolvedCertificateFactoid.factoidText,
      variantColor: resolvedCertificateFactoid.variantColor,
      tooltip: resolvedCertificateFactoid.tooltip,
    };

    courseInformation.splice(
      resolvedCertificateFactoid.position,
      0,
      factoidItem,
    );
  }

  // check if there is a promo highlight and get the placement position
  const promoPosition = resolvedCertificateFactoid.position;
  const hasPromoFactoid = resolvedCertificateFactoid.variantColor === "primary";
  const promoFactoidPosition =
    promoPosition && promoPosition >= courseInformation?.length
      ? courseInformation?.length - 1
      : Number(promoPosition);

  const certificateFactoidClassName = `${resolvedCertificateFactoid.showFactoidMobile ? "block" : "hidden"} ${resolvedCertificateFactoid.showFactoidDesktop ? "md:block" : "md:hidden"}`;

  // this function keeps the factoids icons on the right column aligned
  const getIconAlignmentStyle = (key: number, variantColor?: string) => {
    if (
      hasPromoFactoid &&
      variantColor !== "primary" &&
      promoFactoidPosition % 2 === 1 &&
      key % 2 === 1
    ) {
      return "lg:ml-4";
    }

    return "";
  };

  const IconTextListItems: FC = () =>
    courseInformation.map((item, key) => {
      const addAlignIconClass = getIconAlignmentStyle(key, item?.variantColor);

      const descriptionOverride =
        item.header === tCourse("instructorPaced")
          ? tCourse("scheduledClasses")
          : null;
      return (
        <IconTextListItem
          key={key}
          iconSize="42"
          icon={item.icon}
          header={item.header}
          boldHeader={true}
          description={descriptionOverride ?? item.description}
          small
          className={`${item.icon === "certificate" ? certificateFactoidClassName : ""} ${addAlignIconClass}`}
          variantColor={item?.variantColor || "default"}
          tooltip={item.tooltip}
        />
      );
    });

  return (
    <div className="flex w-full mt-4">
      <IconTextGrid
        fullwidth={false}
        noWrap={false}
        small
        className="w-full px-0"
        gridClassName="gap-y-0 md:gap-y-4"
      >
        <IconTextListItems />
      </IconTextGrid>
    </div>
  );
}
